import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Context } from "../../store/AppContext";
import Head from "../Head";
import IssuerCompanySelector from "./IssuerCompanySelector";
import SellerSummary from "./SellerSummary";
import ClientForm from "./ClientForm";
import QuoteItemsSection from "./QuoteItemsSection";
import QuoteDiscountSection from "./QuoteDiscountSection";
import QuoteTotals from "./QuoteTotals";
import PaymentConditionsForm from "./PaymentConditionsForm";
import QuotePreview from "./QuotePreview";
import { issuerCompanyRepository } from "../../Helper/Cotizacion/issuerCompanyRepository";
import { quoteRepository } from "../../Helper/Cotizacion/quoteRepository";
import { getCurrentSeller } from "../../Helper/Cotizacion/currentUserAdapter";
import { createIssuerSnapshot, getCompanyById } from "../../Helper/Cotizacion/mockCompanies";
import { isValidRut } from "../../Helper/Cotizacion/rutUtils";
import {
	applyTotalsToQuote,
	canEmitQuote,
	createEmptyItem,
	createEmptyQuote,
	generateMockQuoteNumber,
} from "../../Helper/Cotizacion/quoteModel";
import { downloadQuotePdf } from "../../Helper/Cotizacion/quotePdf";
import { MAX_QUOTE_ITEMS } from "../../Helper/Cotizacion/mockProducts";

const CotizacionFormPage = ({ mode = "create" }) => {
	const { store } = useContext(Context);
	const navigate = useNavigate();
	const { id } = useParams();
	const seller = useMemo(() => getCurrentSeller(store), [store.usuarioActual]);

	const [companies, setCompanies] = useState([]);
	const [quote, setQuote] = useState(null);
	const [companySelected, setCompanySelected] = useState(false);
	const [showPreviewModal, setShowPreviewModal] = useState(false);
	const [message, setMessage] = useState("");
	const [errors, setErrors] = useState([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (store.usuarioActual == null && store.token == null) {
			const timer = setTimeout(() => navigate("/"), 200);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [store.usuarioActual, store.token, navigate]);

	useEffect(() => {
		let active = true;
		const bootstrap = async () => {
			setLoading(true);
			const companyList = await issuerCompanyRepository.list();
			if (!active) return;
			setCompanies(companyList);

			if (mode === "edit" && id) {
				const existing = await quoteRepository.getById(id);
				if (!existing) {
					setMessage("Cotización no encontrada.");
					setLoading(false);
					return;
				}
				if (existing.status !== "borrador") {
					setMessage("Solo se pueden editar cotizaciones en borrador.");
					setQuote(applyTotalsToQuote(existing));
					setCompanySelected(Boolean(existing.issuerCompanyId));
					setLoading(false);
					return;
				}
				setQuote(applyTotalsToQuote(existing));
				setCompanySelected(Boolean(existing.issuerCompanyId));
			} else {
				setQuote(createEmptyQuote({ seller }));
				setCompanySelected(false);
			}
			setLoading(false);
		};
		bootstrap();
		return () => {
			active = false;
		};
	}, [mode, id, seller]);

	const updateQuote = (partial) => {
		setQuote((prev) => applyTotalsToQuote({ ...prev, ...partial }));
		setMessage("");
		setErrors([]);
	};

	const handleSelectCompany = (company) => {
		updateQuote({
			issuerCompanyId: company.id,
			issuerSnapshot: createIssuerSnapshot(company),
			taxRate: company.tipoTributario === "afecta" ? company.tasaIva : 0,
		});
		setCompanySelected(true);
	};

	const handleChangeCompany = () => {
		setCompanySelected(false);
	};

	const handleChangeItem = (index, nextItem) => {
		const items = [...quote.items];
		items[index] = nextItem;
		updateQuote({ items });
	};

	const handleAddItem = () => {
		if (quote.items.length >= MAX_QUOTE_ITEMS) return;
		updateQuote({ items: [...quote.items, createEmptyItem()] });
	};

	const handleRemoveItem = (index) => {
		updateQuote({ items: quote.items.filter((_, i) => i !== index) });
	};

	const validateDraftBasics = () => {
		const localErrors = [];
		if (!quote.issuerCompanyId) localErrors.push("Debes seleccionar una empresa emisora.");
		if (quote.client?.rut && !isValidRut(quote.client.rut)) {
			localErrors.push("El RUT del cliente no es válido.");
		}
		return localErrors;
	};

	const handleSaveDraft = async () => {
		const localErrors = validateDraftBasics();
		if (localErrors.length) {
			setErrors(localErrors);
			return;
		}
		const payload = applyTotalsToQuote({
			...quote,
			status: "borrador",
			sellerId: seller.id,
			sellerSnapshot: {
				nombre: seller.nombre,
				correo: seller.correo,
				telefono: seller.telefono,
				rol: seller.rol,
			},
		});

		try {
			if (mode === "edit" && id) {
				await quoteRepository.update(id, payload);
				setQuote(payload);
				setMessage("Borrador actualizado correctamente.");
			} else {
				const created = await quoteRepository.create(payload);
				setMessage("Borrador guardado correctamente.");
				navigate(`/cotizacion/editar/${created.id}`);
			}
		} catch (error) {
			setErrors([error.message || "No se pudo guardar el borrador."]);
		}
	};

	const handleEmit = async () => {
		const localErrors = validateDraftBasics();
		const emitCheck = canEmitQuote(quote);
		const allErrors = [...localErrors, ...emitCheck.errors];
		if (quote.client?.rut && !isValidRut(quote.client.rut)) {
			allErrors.push("El RUT del cliente no es válido.");
		}
		if (allErrors.length) {
			setErrors([...new Set(allErrors)]);
			return;
		}

		const existing = await quoteRepository.list();
		const company =
			getCompanyById(quote.issuerCompanyId) ||
			quote.issuerSnapshot ||
			null;
		const number = generateMockQuoteNumber(company, existing);
		const payload = applyTotalsToQuote({
			...quote,
			number,
			status: "emitida",
			issuedAt: new Date().toISOString(),
			sellerId: seller.id,
			sellerSnapshot: {
				nombre: seller.nombre,
				correo: seller.correo,
				telefono: seller.telefono,
				rol: seller.rol,
			},
		});

		try {
			let saved;
			if (mode === "edit" && id) {
				saved = await quoteRepository.update(id, payload);
			} else {
				saved = await quoteRepository.create(payload);
			}
			setMessage(`Cotización emitida: ${saved.number}`);
			navigate(`/cotizacion/ver/${saved.id}`);
		} catch (error) {
			setErrors([error.message || "No se pudo emitir la cotización."]);
		}
	};

	if (loading || !quote) {
		return (
			<div className="no-print">
				<Head
					contenido={[
						mode === "edit" ? "Editar cotización" : "Nueva cotización",
						"",
					]}
				/>
				<div className="grid-container fluid" style={{ padding: "16px" }}>
					<p>{message || "Cargando..."}</p>
					<Link to="/cotizacion">Volver al listado</Link>
				</div>
			</div>
		);
	}

	const readOnly = mode === "edit" && quote.status !== "borrador";

	return (
		<div className="no-print">
			<Head
				contenido={[
					mode === "edit" ? "Editar cotización" : "Nueva cotización",
					"Mockup local",
				]}
			/>
			<div className="grid-container fluid" style={{ padding: "0 16px 40px" }}>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						flexWrap: "wrap",
						gap: "8px",
						marginBottom: "12px",
					}}
				>
					<div>
						<p style={{ marginBottom: 0, color: "#4a5568" }}>
							{mode === "edit"
								? `Editando ${quote.number || "borrador"}`
								: "Completa los datos y guarda o emite la cotización."}
						</p>
					</div>
					<Link to="/cotizacion" className="button hollow secondary" style={{ marginBottom: 0 }}>
						Volver al listado
					</Link>
				</div>

				{message ? (
					<div className="callout success" style={{ marginBottom: "12px" }}>
						{message}
					</div>
				) : null}
				{errors.length > 0 ? (
					<div className="callout alert" style={{ marginBottom: "12px" }}>
						<ul style={{ marginBottom: 0 }}>
							{errors.map((error) => (
								<li key={error}>{error}</li>
							))}
						</ul>
					</div>
				) : null}

				{!companySelected || !quote.issuerCompanyId ? (
					<IssuerCompanySelector
						companies={companies}
						selectedId={quote.issuerCompanyId}
						onSelect={handleSelectCompany}
					/>
				) : (
					<>
						{!readOnly ? (
							<section style={{ marginBottom: "24px" }}>
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										flexWrap: "wrap",
										gap: "8px",
										marginBottom: "8px",
									}}
								>
									<h5 style={{ marginBottom: 0 }}>1. Empresa emisora</h5>
									<button
										type="button"
										className="button hollow secondary tiny"
										style={{ marginBottom: 0 }}
										onClick={handleChangeCompany}
									>
										Cambiar empresa emisora
									</button>
								</div>
								<div
									style={{
										display: "flex",
										gap: "12px",
										alignItems: "center",
										border: "1px solid #e2e8f0",
										borderRadius: "8px",
										padding: "12px",
										background: "#fff",
									}}
								>
									<div
										style={{
											width: "48px",
											height: "48px",
											borderRadius: "8px",
											background: quote.issuerSnapshot.logoColor || "#2c3840",
											color: "#fff",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											fontWeight: 700,
										}}
									>
										{quote.issuerSnapshot.logo || "DN"}
									</div>
									<div>
										<strong>
											{quote.issuerSnapshot.nombreFantasia ||
												quote.issuerSnapshot.nombreLegal}
										</strong>
										<div style={{ fontSize: "13px" }}>
											RUT {quote.issuerSnapshot.rut}
										</div>
										<div
											style={{
												fontSize: "13px",
												fontWeight: 600,
												color:
													quote.issuerSnapshot.tipoTributario === "afecta"
														? "#2b6cb0"
														: "#276749",
											}}
										>
											{quote.issuerSnapshot.tipoTributario === "afecta"
												? "Empresa afecta a IVA (19%)"
												: "Empresa exenta de IVA"}
										</div>
									</div>
								</div>
							</section>
						) : null}

						<SellerSummary seller={seller} />

						{!readOnly ? (
							<>
								<ClientForm
									client={quote.client}
									onChange={(client) => updateQuote({ client })}
								/>
								<QuoteItemsSection
									items={quote.items}
									onChangeItem={handleChangeItem}
									onAddItem={handleAddItem}
									onRemoveItem={handleRemoveItem}
								/>
								<QuoteDiscountSection
									globalDiscount={quote.globalDiscount}
									onChange={(globalDiscount) => updateQuote({ globalDiscount })}
								/>
								<QuoteTotals quote={quote} />
								<PaymentConditionsForm
									quote={quote}
									onChange={(partial) => updateQuote(partial)}
								/>
							</>
						) : null}

						<section style={{ marginBottom: "24px" }}>
							<h5 style={{ marginBottom: "8px" }}>6. Vista previa</h5>
							<QuotePreview quote={quote} />
						</section>

						{!readOnly ? (
							<div
								style={{
									display: "flex",
									flexWrap: "wrap",
									gap: "8px",
									position: "sticky",
									bottom: "8px",
									background: "rgba(255,255,255,0.95)",
									padding: "10px",
									border: "1px solid #e2e8f0",
									borderRadius: "8px",
								}}
							>
								<button
									type="button"
									className="button secondary"
									style={{ marginBottom: 0 }}
									onClick={handleSaveDraft}
								>
									Guardar borrador
								</button>
								<button
									type="button"
									className="button hollow"
									style={{ marginBottom: 0 }}
									onClick={() => setShowPreviewModal(true)}
								>
									Vista previa
								</button>
								<button
									type="button"
									className="button success"
									style={{ marginBottom: 0 }}
									onClick={handleEmit}
								>
									Emitir cotización
								</button>
								<button
									type="button"
									className="button"
									style={{ marginBottom: 0 }}
									onClick={() => downloadQuotePdf(applyTotalsToQuote(quote))}
								>
									Descargar PDF
								</button>
								<button
									type="button"
									className="button hollow alert"
									style={{ marginBottom: 0 }}
									onClick={() => navigate("/cotizacion")}
								>
									Cancelar
								</button>
							</div>
						) : null}
					</>
				)}
			</div>

			{showPreviewModal ? (
				<div
					style={{
						position: "fixed",
						inset: 0,
						background: "rgba(0,0,0,0.45)",
						zIndex: 9999,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						padding: "16px",
					}}
					onClick={() => setShowPreviewModal(false)}
				>
					<div
						style={{
							background: "#fff",
							borderRadius: "10px",
							maxWidth: "900px",
							width: "100%",
							maxHeight: "90vh",
							overflow: "auto",
							padding: "16px",
						}}
						onClick={(event) => event.stopPropagation()}
					>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								marginBottom: "12px",
							}}
						>
							<h4 style={{ margin: 0 }}>Vista previa</h4>
							<button
								type="button"
								className="button hollow tiny"
								style={{ marginBottom: 0 }}
								onClick={() => setShowPreviewModal(false)}
							>
								Cerrar
							</button>
						</div>
						<QuotePreview quote={quote} />
					</div>
				</div>
			) : null}
		</div>
	);
};

export default CotizacionFormPage;
